import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, CardBody, Input, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const GST_SLABS = [0, 3, 5, 12, 18, 28];

// Shared field styling — matches the @terra/ui Input metrics.
const fieldClass =
  'border-border-strong/40 bg-surface text-ink h-11 w-full rounded-xl border px-3.5 text-sm transition-all duration-200 hover:border-border-strong/70 focus:border-gold-400 focus:outline-none';
const textareaClass =
  'border-border-strong/40 bg-surface text-ink w-full rounded-xl border px-3.5 py-2.5 text-sm transition-all duration-200 hover:border-border-strong/70 focus:border-gold-400 focus:outline-none';
const cellInputClass =
  'border-border-strong/40 bg-surface text-ink h-9 w-full rounded-lg border px-2.5 text-xs transition-all duration-200 focus:border-gold-400 focus:outline-none';

interface VariantField {
  sku: string;
  barcode: string;
  mrp: string; // rupees
  price: string; // rupees
  weightGrams: string;
  /** Packed box size, centimetres; blank sends none (the courier gets an estimate). */
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  /** Opening stock, units; blank books none. Only sent when creating. */
  stock: string;
  optionValues: Record<string, string>;
}

/** Box size in whole millimetres, as the API stores it; blank or invalid sides are left out. */
function packedSize(v: { lengthCm: string; widthCm: string; heightCm: string }) {
  const mm = (cm: string) => {
    const n = parseFloat(cm);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 10) : undefined;
  };
  const [lengthMm, widthMm, heightMm] = [mm(v.lengthCm), mm(v.widthCm), mm(v.heightCm)];
  return {
    ...(lengthMm ? { lengthMm } : {}),
    ...(widthMm ? { widthMm } : {}),
    ...(heightMm ? { heightMm } : {}),
  };
}

interface ImageField {
  fileId: string;
  url: string;
  alt: string;
}

/** Section heading inside a card. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="border-border text-ink border-b pb-3 text-[15px] font-semibold tracking-tight">
      {children}
    </h2>
  );
}

export function CreateProductPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id: productId } = useParams();
  const isEdit = !!productId;

  // ── States ──
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [vendorId, setVendorId] = useState('');
  // The platform's own product, sold in-house (listed under In-House Products).
  const [isInHouse, setIsInHouse] = useState(false);
  const [brandId, setBrandId] = useState('');
  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [customBrandName, setCustomBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [bulletPoints, setBulletPoints] = useState<string[]>(['']);
  const [hsnSac, setHsnSac] = useState('');
  const [gstRate, setGstRate] = useState<number>(18);
  // Vendor's homepage placement request. '' = no request. Admin reviews
  // and decides the actual placement on approval.
  const [requestedHomepageSection, setRequestedHomepageSection] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState('');

  // Variants management
  const [hasVariants, setHasVariants] = useState(false);
  const [options, setOptions] = useState<Array<{ name: string; values: string[] }>>([
    { name: 'Color', values: [] },
  ]);
  const [singleVariant, setSingleVariant] = useState<VariantField>({
    sku: '',
    barcode: '',
    mrp: '',
    price: '',
    weightGrams: '100',
    lengthCm: '',
    widthCm: '',
    heightCm: '',
    stock: '',
    optionValues: {},
  });
  const [variantGrid, setVariantGrid] = useState<VariantField[]>([]);

  // Media management
  const [media, setMedia] = useState<ImageField[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageAlt, setNewImageAlt] = useState('');

  // ── Queries ──
  // Fetch active vendors
  const vendorsQuery = useQuery({
    queryKey: ['vendors-min'],
    queryFn: () => api.vendors.list({ pageSize: 100 }),
  });

  // Fetch active categories
  const categoriesQuery = useQuery({
    queryKey: ['categories-list'],
    queryFn: () => api.categories.list({ active: true }),
  });

  // Fetch active brands
  const brandsQuery = useQuery({
    queryKey: ['brands-list'],
    queryFn: () => api.catalog.listBrandsPublic(),
  });

  // Fetch active attributes
  const attributesQuery = useQuery({
    queryKey: ['attributes-list'],
    queryFn: () => api.catalog.listAttributesPublic(),
  });

  // Fetch product if editing
  const productQuery = useQuery({
    queryKey: ['vendor-product-detail', productId],
    queryFn: () => api.products.getBySlug(productId!), // Fetching by slug or ID
    enabled: isEdit,
  });

  // ── Auto Sluggify title ──
  useEffect(() => {
    if (!isEdit && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      setSlug(generatedSlug);
    }
  }, [title, isEdit]);

  // ── Populate data if editing ──
  useEffect(() => {
    if (isEdit && productQuery.data) {
      const p = productQuery.data;
      setTitle(p.title);
      setSlug(p.slug);
      if (p.category?.id) {
        setCategoryId(p.category.id);
        setIsCustomCategory(false);
      }
      if (p.brandId) {
        setBrandId(p.brandId);
        setIsCustomBrand(false);
      } else if (p.brand) {
        setCustomBrandName(p.brand);
        setIsCustomBrand(true);
      }
      setDescription(p.description ?? '');
      setBulletPoints(p.bulletPoints && p.bulletPoints.length > 0 ? p.bulletPoints : ['']);
      setHsnSac(p.hsnSac ?? '');
      setGstRate(p.gstRate ?? 18);
      setRequestedHomepageSection(p.requestedHomepageSection ?? '');

      // Load media
      if (p.media && p.media.length > 0) {
        setMedia(
          p.media.map((m: any) => ({
            fileId: m.id ?? `img_${Math.random().toString(36).substring(7)}`,
            url: m.url,
            alt: m.alt ?? '',
          })),
        );
      }

      // Load variants
      if (p.variants && p.variants.length > 0) {
        const first = p.variants[0];
        if (first) {
          const isMulti = Object.keys(first.optionValues ?? {}).length > 0;
          setHasVariants(isMulti);

          if (!isMulti) {
            setSingleVariant({
              sku: first.sku ?? '',
              barcode: (first as any).barcode ?? '',
              mrp: ((first.mrpPaise ?? 0) / 100).toString(),
              price: ((first.pricePaise ?? 0) / 100).toString(),
              weightGrams: ((first as any).weightGrams ?? 100).toString(),
              lengthCm: (first as any).lengthMm ? String((first as any).lengthMm / 10) : '',
              widthCm: (first as any).widthMm ? String((first as any).widthMm / 10) : '',
              heightCm: (first as any).heightMm ? String((first as any).heightMm / 10) : '',
              stock: '',
              optionValues: {},
            });
          } else {
            // Rebuild variant options
            const keys = Object.keys(first.optionValues ?? {});
            const rebuiltOptions = keys.map((key) => {
              const vals = Array.from(
                new Set(p.variants.map((v: any) => v.optionValues?.[key]).filter(Boolean)),
              );
              return { name: key, values: vals as string[] };
            });
            setOptions(rebuiltOptions);

            setVariantGrid(
              p.variants.map((v: any) => ({
                sku: v.sku ?? '',
                barcode: v.barcode ?? '',
                mrp: ((v.mrpPaise ?? 0) / 100).toString(),
                price: ((v.pricePaise ?? 0) / 100).toString(),
                weightGrams: (v.weightGrams ?? 100).toString(),
                lengthCm: v.lengthMm ? String(v.lengthMm / 10) : '',
                widthCm: v.widthMm ? String(v.widthMm / 10) : '',
                heightCm: v.heightMm ? String(v.heightMm / 10) : '',
                stock: '',
                optionValues: v.optionValues ?? {},
              })),
            );
          }
        }
      }
    }
  }, [isEdit, productQuery.data]);

  // ── Helper: generate combinations matrix ──
  const generateCombinations = useCallback(
    (existingGrid: VariantField[]): VariantField[] => {
      // filter valid options
      const activeOpts = options.filter((o) => o.name.trim() && o.values.length > 0);
      if (activeOpts.length === 0) {
        return [];
      }

      const helper = (depth: number, current: Record<string, string>): VariantField[] => {
        if (depth === activeOpts.length) {
          // Find existing match to preserve manual input values (prices, MRPs, barcodes, etc.)
          const match = existingGrid.find((v) => {
            return (
              Object.entries(current).every(([key, val]) => v.optionValues[key] === val) &&
              Object.keys(current).length === Object.keys(v.optionValues).length
            );
          });

          if (match) {
            // If match found, update its SKU if slug changes but keep the rest
            if (slug && !match.sku.startsWith(slug)) {
              match.sku = `${slug}-${Object.values(current).join('-').toLowerCase()}`;
            }
            return [match];
          }

          return [
            {
              sku: `${slug || 'prod'}-${Object.values(current).join('-').toLowerCase()}`,
              barcode: '',
              mrp: singleVariant.mrp || '1000',
              price: singleVariant.price || '800',
              weightGrams: singleVariant.weightGrams || '100',
              lengthCm: singleVariant.lengthCm,
              widthCm: singleVariant.widthCm,
              heightCm: singleVariant.heightCm,
              stock: singleVariant.stock || '',
              optionValues: { ...current },
            },
          ];
        }

        const opt = activeOpts[depth];
        if (!opt) return [];
        return opt.values.flatMap((val) => helper(depth + 1, { ...current, [opt.name]: val }));
      };

      return helper(0, {});
    },
    [
      options,
      slug,
      singleVariant.mrp,
      singleVariant.price,
      singleVariant.weightGrams,
      singleVariant.lengthCm,
      singleVariant.widthCm,
      singleVariant.heightCm,
      singleVariant.stock,
    ],
  );

  // Generate grid when options or slug changes, while preserving typed input cells
  useEffect(() => {
    if (hasVariants) {
      setVariantGrid((prev) => generateCombinations(prev));
    }
  }, [hasVariants, generateCombinations]);

  // Option actions
  const addOption = () => {
    setOptions([...options, { name: '', values: [] }]);
  };

  const removeOption = (index: number) => {
    const next = [...options];
    next.splice(index, 1);
    setOptions(next);
  };

  const updateOptionName = (index: number, name: string) => {
    const next = [...options];
    if (next[index]) {
      next[index].name = name;
      setOptions(next);
    }
  };

  const addOptionValue = (index: number, val: string) => {
    if (!val.trim()) return;
    const next = [...options];
    if (next[index] && !next[index].values.includes(val)) {
      next[index].values = [...next[index].values, val];
      setOptions(next);
    }
  };

  const removeOptionValue = (optIndex: number, valIndex: number) => {
    const next = [...options];
    if (next[optIndex]) {
      next[optIndex].values.splice(valIndex, 1);
      setOptions(next);
    }
  };

  // Bullet points
  const handleAddBullet = () => setBulletPoints([...bulletPoints, '']);
  const handleRemoveBullet = (i: number) => {
    const next = [...bulletPoints];
    next.splice(i, 1);
    setBulletPoints(next);
  };
  const handleBulletChange = (i: number, val: string) => {
    const next = [...bulletPoints];
    next[i] = val;
    setBulletPoints(next);
  };

  // Media
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg('');
    setUploading(true);

    try {
      // 1. Fetch secure signature from backend
      const sigData = await api.files.getUploadSignature({ folder: 'products' });

      // 2. Build Cloudinary form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sigData.apiKey);
      formData.append('timestamp', sigData.timestamp.toString());
      formData.append('signature', sigData.signature);
      formData.append('folder', sigData.folder);

      // 3. Post to Cloudinary
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sigData.cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.error?.message || 'Cloudinary upload failed');
      }

      const uploadResult = await res.json();

      // 4. Register in database
      const registered = await api.files.register({
        name: file.name,
        url: uploadResult.secure_url,
        contentType: file.type || 'image/*',
        sizeBytes: file.size,
        visibility: 'PUBLIC',
      });

      // 5. Register in state
      setMedia((prev) => [
        ...prev,
        {
          fileId: registered.id,
          url: uploadResult.secure_url,
          alt: file.name.split('.')[0] || 'Uploaded Image',
        },
      ]);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddImageUrl = async () => {
    if (!newImageUrl.trim()) return;
    const cleanUrl = newImageUrl.trim();
    const cleanAlt = newImageAlt.trim() || title || 'Product Image';

    try {
      setUploading(true);
      setErrorMsg('');
      const fileName = cleanUrl.split('/').pop() || 'image.jpg';
      const registered = await api.files.register({
        name: fileName,
        url: cleanUrl,
        contentType: 'image/*',
        visibility: 'PUBLIC',
      });

      setMedia((prev) => [...prev, { fileId: registered.id, url: cleanUrl, alt: cleanAlt }]);
      setNewImageUrl('');
      setNewImageAlt('');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to register image URL');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    const next = [...media];
    next.splice(index, 1);
    setMedia(next);
  };

  // ── Mutations ──
  const mutation = useMutation({
    mutationFn: (payload: Record<string, any>) => {
      return api.products.createProductAdmin(vendorId, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-products'] });
      void qc.invalidateQueries({ queryKey: ['catalog-products'] });
      navigate('/products/all');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to save product';
      setErrorMsg(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    // Validations
    if (!title.trim()) return setErrorMsg('Title is required');
    if (!slug.trim() || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      return setErrorMsg(
        'Slug must be alphanumeric lower-case words separated by hyphens (e.g. brand-new-product)',
      );
    }
    if (!vendorId) return setErrorMsg('Please select a Vendor');
    if (isCustomCategory) {
      if (!customCategoryName.trim()) {
        return setErrorMsg('Please enter a custom Category name');
      }
    } else {
      if (!categoryId) return setErrorMsg('Please select a Category');
    }
    if (!hsnSac.trim() || !/^\d{4,8}$/.test(hsnSac)) {
      return setErrorMsg('HSN/SAC must be between 4 to 8 digits');
    }

    // Build variants payload
    let variantsPayload: any[] = [];
    if (!hasVariants) {
      if (!singleVariant.mrp || !singleVariant.price) {
        return setErrorMsg('Please enter MRP and Price for the product');
      }
      variantsPayload = [
        {
          optionValues: {},
          mrpPaise: Math.round(parseFloat(singleVariant.mrp) * 100),
          pricePaise: Math.round(parseFloat(singleVariant.price) * 100),
          weightGrams: parseInt(singleVariant.weightGrams) || 100,
          ...packedSize(singleVariant),
          sku: singleVariant.sku.trim() || `${slug}-default`,
          barcode: singleVariant.barcode.trim() || undefined,
          ...(singleVariant.stock.trim() !== ''
            ? { initialStock: Math.max(0, parseInt(singleVariant.stock, 10) || 0) }
            : {}),
        },
      ];
    } else {
      if (variantGrid.length === 0) {
        return setErrorMsg('Please add option values to generate at least one variant combination');
      }
      for (const row of variantGrid) {
        if (!row.mrp || !row.price) {
          return setErrorMsg(
            `Please enter Price and MRP details for variant combination: ${Object.values(row.optionValues).join('/')}`,
          );
        }
        variantsPayload.push({
          optionValues: row.optionValues,
          mrpPaise: Math.round(parseFloat(row.mrp) * 100),
          pricePaise: Math.round(parseFloat(row.price) * 100),
          weightGrams: parseInt(row.weightGrams) || 100,
          ...packedSize(row),
          sku: row.sku.trim() || undefined,
          barcode: row.barcode.trim() || undefined,
          ...(row.stock.trim() !== ''
            ? { initialStock: Math.max(0, parseInt(row.stock, 10) || 0) }
            : {}),
        });
      }
    }

    // Prepare media
    const mediaPayload = media.map((m) => ({
      fileId: m.fileId,
      url: m.url,
      alt: m.alt || undefined,
    }));

    // Filter bullet points
    const bulletPayload = bulletPoints.filter((b) => b.trim());

    const payload: Record<string, any> = {
      title: title.trim(),
      ...(isCustomCategory ? { categoryName: customCategoryName.trim() } : { categoryId }),
      ...(isCustomBrand
        ? { brand: customBrandName.trim() || undefined }
        : { brandId: brandId || undefined, brand: customBrandName.trim() || undefined }),
      description: description.trim() || undefined,
      bulletPoints: bulletPayload.length > 0 ? bulletPayload : undefined,
      hsnSac: hsnSac.trim(),
      gstRate,
      requestedHomepageSection: requestedHomepageSection || null,
      media: mediaPayload.length > 0 ? mediaPayload : undefined,
    };

    if (!isEdit) {
      payload.slug = slug.trim();
      payload.variants = variantsPayload;
      if (isInHouse) payload.isFirstParty = true;
    }

    mutation.mutate(payload);
  };

  if (isEdit && productQuery.isLoading) {
    return (
      <div className="grid min-h-[400px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEdit ? 'Edit product' : 'Create product'}
        description={
          isEdit
            ? `Update details for "${productQuery.data?.title}"`
            : 'Add a brand new item to your storefront catalog.'
        }
      />

      {errorMsg && (
        <div className="border-state-danger/30 bg-state-danger-bg text-state-danger flex items-center gap-2.5 rounded-xl border p-4 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-5">
        {/* ── CARD 1: CORE DATA ── */}
        <Card>
          <CardBody className="space-y-4">
            <SectionTitle>1. Core details</SectionTitle>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Product title *"
                placeholder="e.g. Premium Wireless Over-Ear Headphones"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <Input
                label="Friendly URL slug *"
                placeholder="e.g. premium-wireless-headphones"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-ink text-[13px] font-semibold">Vendor *</label>
                {vendorsQuery.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <select
                    className={fieldClass}
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    required
                  >
                    <option value="">Select vendor</option>
                    {(vendorsQuery.data?.data ?? []).map((v: any) => (
                      <option key={v.id} value={v.id}>
                        {v.storeName}
                      </option>
                    ))}
                  </select>
                )}
                {!isEdit && (
                  <label className="text-ink-muted mt-1 flex items-start gap-2 text-[12px] font-medium">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={isInHouse}
                      onChange={(e) => setIsInHouse(e.target.checked)}
                    />
                    <span>
                      <b className="text-ink">In-house product</b> — sold by Terra Shoppy itself.
                      Pick the platform&apos;s own store above; it ships from that store&apos;s
                      warehouse.
                    </span>
                  </label>
                )}
              </div>

              {/* Category Selection or Custom Input */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-ink text-[13px] font-semibold">Category *</label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomCategory((prev) => !prev);
                      if (!isCustomCategory) {
                        setCategoryId('');
                      } else {
                        setCustomCategoryName('');
                      }
                    }}
                    className="text-gold-600 hover:text-gold-700 text-xs font-semibold underline decoration-dotted transition-colors"
                  >
                    {isCustomCategory ? '← Select from list' : '+ Type new category'}
                  </button>
                </div>
                {categoriesQuery.isLoading ? (
                  <Spinner size="sm" />
                ) : isCustomCategory ? (
                  <div className="space-y-1">
                    <Input
                      placeholder="Type custom category (e.g. Handmade Crafts)"
                      value={customCategoryName}
                      onChange={(e) => setCustomCategoryName(e.target.value)}
                      required
                    />
                    <span className="text-gold-600 block text-[11px] font-medium">
                      ✨ New category will be created automatically
                    </span>
                  </div>
                ) : (
                  <select
                    className={fieldClass}
                    value={categoryId}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setIsCustomCategory(true);
                        setCategoryId('');
                      } else {
                        setCategoryId(e.target.value);
                      }
                    }}
                    required
                  >
                    <option value="">Select category</option>
                    {(categoriesQuery.data ?? []).map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                    <option value="__custom__">+ Type new category...</option>
                  </select>
                )}
              </div>

              {/* Brand Selection or Custom Input */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-ink text-[13px] font-semibold">Brand (optional)</label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomBrand((prev) => !prev);
                      if (!isCustomBrand) {
                        setBrandId('');
                      } else {
                        setCustomBrandName('');
                      }
                    }}
                    className="text-gold-600 hover:text-gold-700 text-xs font-semibold underline decoration-dotted transition-colors"
                  >
                    {isCustomBrand ? '← Select from list' : '+ Type custom brand'}
                  </button>
                </div>
                {brandsQuery.isLoading ? (
                  <Spinner size="sm" />
                ) : isCustomBrand ? (
                  <div className="space-y-1">
                    <Input
                      placeholder="Type custom brand (e.g. Artisan Luxe)"
                      value={customBrandName}
                      onChange={(e) => setCustomBrandName(e.target.value)}
                    />
                    <span className="text-gold-600 block text-[11px] font-medium">
                      ✨ Custom brand name will be saved
                    </span>
                  </div>
                ) : (
                  <select
                    className={fieldClass}
                    value={brandId}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setIsCustomBrand(true);
                        setBrandId('');
                      } else {
                        setBrandId(e.target.value);
                      }
                    }}
                  >
                    <option value="">No Brand</option>
                    {(brandsQuery.data ?? [])
                      .filter((b: any) => b.active)
                      .map((b: any) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    <option value="__custom__">+ Type custom brand...</option>
                  </select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="HSN/SAC code *"
                  placeholder="e.g. 8517"
                  value={hsnSac}
                  onChange={(e) => setHsnSac(e.target.value)}
                  required
                />
                <div className="flex flex-col gap-1.5">
                  <label className="text-ink text-[13px] font-semibold">GST rate *</label>
                  <select
                    className={fieldClass}
                    value={gstRate}
                    onChange={(e) => setGstRate(Number(e.target.value))}
                  >
                    {GST_SLABS.map((r) => (
                      <option key={r} value={r}>
                        {r}% GST
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-ink text-[13px] font-semibold">
                  Homepage placement <span className="text-ink-muted font-normal">(optional)</span>
                </label>
                <select
                  className={fieldClass}
                  value={requestedHomepageSection}
                  onChange={(e) => setRequestedHomepageSection(e.target.value)}
                >
                  <option value="">— No preference —</option>
                  <option value="POPULAR_PICKS">Popular Picks</option>
                  <option value="BOTTOM_GRID_1">Bottom Grid 1</option>
                  <option value="BOTTOM_GRID_2">Bottom Grid 2</option>
                  <option value="BEST_SELLING">Best Selling</option>
                  <option value="FEATURED">Featured</option>
                </select>
                <span className="text-ink-muted text-[11px]">
                  Suggest where this product should appear on the storefront homepage. Admin reviews
                  the request during approval and may adjust.
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-ink text-[13px] font-semibold">Detailed description</label>
              <textarea
                className={textareaClass}
                rows={5}
                placeholder="Describe your product's core features, materials, warranty, specifications…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Bullet Points */}
            <div className="space-y-2">
              <label className="text-ink block text-[13px] font-semibold">
                Key highlights (bullet points)
              </label>
              {bulletPoints.map((bp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-ink-subtle font-mono text-xs font-semibold">#{i + 1}</span>
                  <div className="flex-1">
                    <Input
                      placeholder="e.g. 40 hours battery life with ANC"
                      value={bp}
                      onChange={(e) => handleBulletChange(i, e.target.value)}
                    />
                  </div>
                  {bulletPoints.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveBullet(i)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={handleAddBullet}>
                <Plus className="h-4 w-4" /> Add bullet point
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* ── CARD 2: MEDIA MANAGEMENT ── */}
        <Card>
          <CardBody className="space-y-4">
            <SectionTitle>2. Product images</SectionTitle>

            {/* Added Images */}
            {media.length > 0 && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {media.map((img, i) => (
                  <div
                    key={img.fileId}
                    className="border-border bg-surface-subtle group relative aspect-square overflow-hidden rounded-xl border"
                  >
                    <img src={img.url} alt={img.alt} className="h-full w-full object-cover" />
                    <div className="bg-zone-950/70 absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 text-center opacity-0 transition-opacity group-hover:opacity-100">
                      <span className="w-full truncate text-xs font-semibold text-white">
                        {img.alt}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => handleRemoveImage(i)}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Custom Image */}
            <div className="border-border grid gap-4 border-t pt-4 md:grid-cols-2">
              {/* Gallery Image Upload Box */}
              <div className="border-border-strong/30 bg-surface-subtle/40 hover:border-gold-400 group relative flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed p-6 text-center transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                />
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Spinner size="md" />
                    <span className="text-ink-muted text-sm font-medium">
                      Uploading to Cloudinary…
                    </span>
                  </div>
                ) : (
                  <div className="pointer-events-none flex flex-col items-center gap-2">
                    <span className="bg-gold-50 text-gold-700 ring-gold-100 grid h-12 w-12 place-items-center rounded-xl ring-1 ring-inset transition-transform duration-300 group-hover:scale-105">
                      <Upload className="h-5 w-5" />
                    </span>
                    <div>
                      <span className="text-ink block text-sm font-semibold">
                        Click to upload from gallery
                      </span>
                      <span className="text-ink-subtle mt-0.5 block text-xs">
                        PNG, JPG, WEBP up to 5MB
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual URL Fallback Box */}
              <div className="border-border bg-surface-subtle/40 flex flex-col justify-between rounded-xl border p-4">
                <span className="text-ink-subtle mb-2 block text-[11px] font-semibold uppercase tracking-wider">
                  Or paste a direct image URL
                </span>
                <div className="space-y-3">
                  <Input
                    label="Custom image URL"
                    placeholder="https://example.com/image.jpg"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                  />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Alt caption (e.g. front view)"
                        value={newImageAlt}
                        onChange={(e) => setNewImageAlt(e.target.value)}
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={handleAddImageUrl}>
                      Add URL
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── CARD 3: VARIANTS & PRICING ── */}
        <Card>
          <CardBody className="space-y-4">
            <div className="border-border flex flex-col gap-2 border-b pb-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-ink text-[15px] font-semibold tracking-tight">
                3. Variants, inventory &amp; pricing
              </h2>
              {!isEdit && (
                <label className="text-ink-muted flex cursor-pointer items-center gap-2 text-[13px] font-medium">
                  Product has multiple sizes / colors?
                  <input
                    type="checkbox"
                    checked={hasVariants}
                    onChange={(e) => setHasVariants(e.target.checked)}
                    className="accent-gold-500 h-4 w-4 rounded"
                  />
                </label>
              )}
            </div>

            {isEdit ? (
              <div className="flex gap-3.5 rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-[13px] font-medium text-amber-800">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                <div className="space-y-1">
                  <span className="block font-bold text-amber-900">
                    Variants and pricing cannot be edited from this form
                  </span>
                  <p className="leading-relaxed">
                    To prevent accidental data loss or conflicts with order history, variants cannot
                    be edited here. To modify prices, inventory stock, SKUs, or barcodes, please use
                    the dedicated <strong>Variants</strong> tab on the product details view.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Option A: Simple Product (No variants) */}
                {!hasVariants && (
                  <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-6">
                    <Input
                      label="Selling price (₹) *"
                      type="number"
                      step="0.01"
                      placeholder="899.00"
                      value={singleVariant.price}
                      onChange={(e) =>
                        setSingleVariant({ ...singleVariant, price: e.target.value })
                      }
                      required
                    />
                    <Input
                      label="M.R.P. (₹) *"
                      type="number"
                      step="0.01"
                      placeholder="1299.00"
                      value={singleVariant.mrp}
                      onChange={(e) => setSingleVariant({ ...singleVariant, mrp: e.target.value })}
                      required
                    />
                    <Input
                      label="SKU (stock id)"
                      placeholder="e.g. HP-WIRE-01"
                      value={singleVariant.sku}
                      onChange={(e) => setSingleVariant({ ...singleVariant, sku: e.target.value })}
                    />
                    <Input
                      label="Barcode"
                      placeholder="e.g. 89012345"
                      value={singleVariant.barcode}
                      onChange={(e) =>
                        setSingleVariant({ ...singleVariant, barcode: e.target.value })
                      }
                    />
                    <Input
                      label="Shiprocket weight (g) *"
                      type="number"
                      placeholder="100"
                      value={singleVariant.weightGrams}
                      onChange={(e) =>
                        setSingleVariant({ ...singleVariant, weightGrams: e.target.value })
                      }
                      required
                    />
                    {/* Opening stock. Without it a new product is born "Out of stock";
                        after creation stock changes on the Inventory page. */}
                    {!isEdit && (
                      <Input
                        label="Stock quantity"
                        type="number"
                        min={0}
                        placeholder="e.g. 50"
                        value={singleVariant.stock}
                        onChange={(e) =>
                          setSingleVariant({ ...singleVariant, stock: e.target.value })
                        }
                      />
                    )}
                    <div className="sm:col-span-2 md:col-span-3 lg:col-span-6">
                      <p className="text-ink mb-1.5 text-[13px] font-semibold">
                        Packed box size (cm) — for the courier
                      </p>
                      <div className="grid max-w-md grid-cols-3 gap-2">
                        {(['lengthCm', 'widthCm', 'heightCm'] as const).map((k, i) => (
                          <Input
                            key={k}
                            type="number"
                            min={0}
                            step="0.1"
                            placeholder={['Length', 'Breadth', 'Height'][i]}
                            value={singleVariant[k]}
                            onChange={(e) =>
                              setSingleVariant({ ...singleVariant, [k]: e.target.value })
                            }
                          />
                        ))}
                      </div>
                      <p className="text-ink-subtle mt-1 text-[11.5px]">
                        Measure the box as packed. Couriers charge on the larger of actual and
                        box-size weight; left blank, an estimate is sent.
                      </p>
                    </div>
                  </div>
                )}

                {/* Option B: Multi Variant Combinations */}
                {hasVariants && (
                  <div className="space-y-4">
                    {/* Variant Options Definitions */}
                    <div className="border-border bg-surface-subtle/50 space-y-3 rounded-xl border p-4">
                      <h4 className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                        Define variant options
                      </h4>

                      {options.map((opt, optIdx) => (
                        <div
                          key={optIdx}
                          className="border-border space-y-2 border-b pb-3 last:border-0 last:pb-0"
                        >
                          <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                            <div className="w-full md:w-60">
                              {attributesQuery.isLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                <select
                                  className={fieldClass}
                                  value={opt.name}
                                  onChange={(e) => updateOptionName(optIdx, e.target.value)}
                                >
                                  <option value="">Select attribute...</option>
                                  {(attributesQuery.data ?? []).map((attr: any) => (
                                    <option key={attr.id} value={attr.name}>
                                      {attr.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <span className="text-ink-subtle shrink-0 text-[11px] font-semibold uppercase tracking-wider">
                              Values:
                            </span>
                            <div className="flex flex-1 flex-wrap items-center gap-1.5">
                              {opt.values.map((v, vIdx) => (
                                <span
                                  key={vIdx}
                                  className="bg-gold-100 text-gold-800 ring-gold-200 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset"
                                >
                                  {v}
                                  <button
                                    type="button"
                                    onClick={() => removeOptionValue(optIdx, vIdx)}
                                    className="hover:text-state-danger"
                                    aria-label={`Remove ${v}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              ))}

                              {/* Option Value Adder Input */}
                              {(() => {
                                const selectedAttr = (attributesQuery.data ?? []).find(
                                  (a: any) => a.name === opt.name,
                                );
                                if (selectedAttr?.values && selectedAttr.values.length > 0) {
                                  return (
                                    <select
                                      className={`${cellInputClass} w-32`}
                                      value=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          addOptionValue(optIdx, e.target.value);
                                        }
                                      }}
                                    >
                                      <option value="">+ Pick value</option>
                                      {selectedAttr.values.map((v: any) => (
                                        <option key={v.id} value={v.value}>
                                          {v.value}
                                        </option>
                                      ))}
                                    </select>
                                  );
                                }
                                return (
                                  <input
                                    type="text"
                                    placeholder="+ Add value"
                                    className={`${cellInputClass} w-28`}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addOptionValue(
                                          optIdx,
                                          (e.target as HTMLInputElement).value,
                                        );
                                        (e.target as HTMLInputElement).value = '';
                                      }
                                    }}
                                    onBlur={(e) => {
                                      addOptionValue(optIdx, e.target.value);
                                      e.target.value = '';
                                    }}
                                  />
                                );
                              })()}
                            </div>

                            {options.length > 1 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeOption(optIdx)}
                              >
                                Remove option
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button type="button" size="sm" variant="outline" onClick={addOption}>
                        <Plus className="h-4 w-4" /> Add new option type
                      </Button>
                    </div>

                    {/* Variant Combination Rows */}
                    {variantGrid.length > 0 && (
                      <div className="ts-scroll border-border bg-surface shadow-card overflow-x-auto rounded-2xl border">
                        <table className="w-full border-collapse text-left text-sm">
                          <thead className="border-border bg-surface-subtle/60 text-ink-subtle border-b text-[11px] font-bold uppercase tracking-[0.08em]">
                            <tr>
                              <th className="px-4 py-3">Variant combination</th>
                              <th className="w-40 px-4 py-3">SKU</th>
                              <th className="w-28 px-4 py-3">Price (₹) *</th>
                              <th className="w-28 px-4 py-3">MRP (₹) *</th>
                              <th className="w-24 px-4 py-3">Weight (g) *</th>
                              <th className="w-32 px-4 py-3">Barcode</th>
                              <th className="w-52 px-4 py-3">Box L × B × H (cm)</th>
                              {!isEdit && <th className="w-24 px-4 py-3">Stock</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-border divide-y">
                            {variantGrid.map((row, idx) => {
                              const label = Object.entries(row.optionValues)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(', ');
                              return (
                                <tr
                                  key={idx}
                                  className="hover:bg-surface-subtle/50 transition-colors"
                                >
                                  <td className="text-gold-700 px-4 py-3 text-xs font-semibold">
                                    {label}
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="text"
                                      className={cellInputClass}
                                      value={row.sku}
                                      onChange={(e) => {
                                        const next = [...variantGrid];
                                        if (next[idx]) {
                                          next[idx].sku = e.target.value;
                                          setVariantGrid(next);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      className={cellInputClass}
                                      placeholder="0.00"
                                      value={row.price}
                                      onChange={(e) => {
                                        const next = [...variantGrid];
                                        if (next[idx]) {
                                          next[idx].price = e.target.value;
                                          setVariantGrid(next);
                                        }
                                      }}
                                      required
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      className={cellInputClass}
                                      placeholder="0.00"
                                      value={row.mrp}
                                      onChange={(e) => {
                                        const next = [...variantGrid];
                                        if (next[idx]) {
                                          next[idx].mrp = e.target.value;
                                          setVariantGrid(next);
                                        }
                                      }}
                                      required
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="number"
                                      className={cellInputClass}
                                      placeholder="100"
                                      value={row.weightGrams}
                                      onChange={(e) => {
                                        const next = [...variantGrid];
                                        if (next[idx]) {
                                          next[idx].weightGrams = e.target.value;
                                          setVariantGrid(next);
                                        }
                                      }}
                                      required
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="text"
                                      className={cellInputClass}
                                      placeholder="Barcode"
                                      value={row.barcode}
                                      onChange={(e) => {
                                        const next = [...variantGrid];
                                        if (next[idx]) {
                                          next[idx].barcode = e.target.value;
                                          setVariantGrid(next);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className="grid grid-cols-3 gap-1">
                                      {(['lengthCm', 'widthCm', 'heightCm'] as const).map((k) => (
                                        <input
                                          key={k}
                                          type="number"
                                          min={0}
                                          step="0.1"
                                          className={cellInputClass}
                                          placeholder={k[0]!.toUpperCase()}
                                          value={row[k]}
                                          onChange={(e) => {
                                            const next = [...variantGrid];
                                            if (next[idx]) {
                                              next[idx][k] = e.target.value;
                                              setVariantGrid(next);
                                            }
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </td>
                                  {!isEdit && (
                                    <td className="px-2 py-2">
                                      <input
                                        type="number"
                                        min={0}
                                        className={cellInputClass}
                                        placeholder="0"
                                        value={row.stock}
                                        onChange={(e) => {
                                          const next = [...variantGrid];
                                          if (next[idx]) {
                                            next[idx].stock = e.target.value;
                                            setVariantGrid(next);
                                          }
                                        }}
                                      />
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* ── ACTION BUTTONS ── */}
        <div className="border-border flex justify-end gap-3 border-t pt-4">
          <Button type="button" variant="outline" onClick={() => navigate('/products')}>
            Cancel
          </Button>
          <Button type="submit" variant="secondary" loading={mutation.isPending}>
            {isEdit ? 'Save product changes' : 'Create product draft'}
          </Button>
        </div>
      </form>
    </div>
  );
}
